import React, { useState } from "react";
import { Scale, ShieldCheck, ChevronRight } from "lucide-react";

export default function TermsOfUse() {
  const [activeSection, setActiveSection] = useState("acceptance");

  const terms = [
    {
      id: "acceptance",
      title: "1. Acceptance of Terms",
      content:
        'Welcome to EduConnectPro, operated by Freshment (a product of GRADCONNECT INNOVATIONS PRIVATE LIMITED) ("Company", "we", "us", or "our"). By accessing or utilizing our platform, services, software, and tools (collectively, the "Service"), you represent and warrant that you have read, understood, and agreed to be bound by these Terms of Use in their entirety. If you are accepting these terms on behalf of a college, university, academic institution, or corporate recruiting partner, you represent warrant that you possess full administrative and legal authority to bind such entity to these conditions. If you do not agree to these terms, you must immediately cease all access and use of our Service.',
    },
    {
      id: "services",
      title: "2. Description of Services & 2FA Enforcement",
      content:
        "EduConnectPro provides a comprehensive, cloud-based campus placement orchestration, credential locker, and recruitment facilitation platform. The Service enables academic institutions to manage student databases, verify academic profiles, curate verified student portfolios, coordinate recruiter communication, receive fresh recruiter/HR contacts, and track active hiring pipelines.\n\nTo guarantee absolute data integrity, EduConnectPro is structured as a mandatory Two-Factor Authentication (2FA) Portal for Security Purposes. Every administrative, recruiter, and student login undergoes strict multi-factor verification to prevent unauthorized access and secure placement channels. We reserve the absolute right to modify, upgrade, suspend, or discontinue any aspect of our Service at any time without prior liability.",
    },
    {
      id: "registration",
      title: "3. Account Security & Verification",
      content:
        "To access the platform's core operational suites, institutions, recruiters, and students must create a verified account and complete the mandatory Two-Factor Authentication (2FA) registration. You agree to provide only accurate, complete, and current registration information. You are solely responsible for maintaining the absolute confidentiality of your login credentials (username, password, or security token) and for all actions, updates, or transmissions executed under your account. Any unauthorized use or security breach must be reported to the Freshment support helpdesk immediately.",
    },
    {
      id: "conduct",
      title: "4. User Conduct & Roster Integrity",
      content:
        "Academic institutions and student users must maintain absolute accuracy and honesty regarding all academic metrics. You are strictly prohibited from: (a) uploading falsified student rosters, inflated GPAs, or fabricated backlog counts; (b) creating duplicate dummy profiles or phantom recruiting entries; (c) scraping, harvesting, or bulk-exporting platform information for unauthorized commercial use; (d) bypassing database access limits or using automated scripts to extract platform contacts; or (e) utilizing the Service to transmit any deceptive or malicious digital contents.",
    },
    {
      id: "payments",
      title: "5. Billing, Subscriptions & Renewals",
      content:
        "Access to specific tier capabilities of EduConnectPro is subject to active subscription fees, as detailed in our pricing schedule or customized institutional service level agreements. Unless explicitly stated otherwise, all subscription packages are billed in advance on a recurring monthly or annual basis and will renew automatically under identical pricing terms. If you wish to cancel or modify your active subscription, you must submit a written request via your institutional dashboard or contact our accounts desk before the beginning of the subsequent billing cycle.",
    },
    {
      id: "intellectual",
      title: "6. Intellectual Property Rights",
      content:
        "All source code, user interfaces, visuals, software architectures, database structures, graphics, brands, and registered trademarks displayed within the Service are the exclusive intellectual property of GRADCONNECT INNOVATIONS PRIVATE LIMITED and are fully protected under global copyright, patent, and trademark laws. Subject to active compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, and revocable license to access and utilize the platform during your active subscription. You may not copy, reverse-engineer, decompile, or distribute any part of our platform.",
    },
    {
      id: "dpdp-compliance",
      title: "7. Compliance with Indian DPDP Act, 2023",
      content:
        "In alignment with the Digital Personal Data Protection (DPDP) Act of 2023 (India), GRADCONNECT INNOVATIONS PRIVATE LIMITED operates as a Data Fiduciary. Personal data processing is restricted to purpose limitations and is executed based on explicit, revocable consent. Under the Act, student candidates (acting as Data Principals) retain all statutory rights, including rights of access, correction, updating, and complete deletion of academic portfolios. Grievance redressal requests can be escalatated to our designated Data Protection Officer.",
    },
    {
      id: "cookie-policy",
      title: "8. Cookie & Session Tracking Policy",
      content:
        "We use essential cookies, performance monitoring cookies, and session-specific cookies to enhance portal experiences and manage secure logged-in states. Essential cookies support our core functionalities, while security-related cookies protect the 2FA system against session-jacking attacks. You can control cookie preferences in your browser settings, though blocking essential cookies may disrupt secure session access.",
    },
    {
      id: "limitation",
      title: "9. Disclaimer of Warranties & Liability",
      content:
        'THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED. GRADCONNECT INNOVATIONS PRIVATE LIMITED AND EDUCONNECTPRO DISCLAIM ALL WARRANTIES, INCLUDING MERCHANTABILITY, FITNESS FOR A SPECIFIC PURPOSE, AND NON-INFRINGEMENT. WE DO NOT GUARANTEE THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR FULLY SECURE, OR THAT ANY SPECIFIC NUMBER OF RECRUITMENT DRIVES, INTERVIEWS, OR CANDIDATE JOB OFFERS WILL BE SECURED. IN NO EVENT SHALL THE COMPANY, ITS DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES ARISING OUT OF YOUR USE OF THE SERVICE.',
    },
    {
      id: "termination",
      title: "10. Platform Suspension & Termination",
      content:
        "We reserve the right, without prior notice and at our sole discretion, to suspend or permanently terminate your institutional or individual account for any material violation of these Terms of Use, non-payment of active fees, or activities that compromise the integrity or security of our database servers. Upon termination, your active license to use the Service is immediately revoked.",
    },
    {
      id: "contact-registry",
      title: "11. Corporate Contact & Registry",
      content:
        "If you have any questions, require further compliance details, or need to initiate formal corporate verification, please connect with us at:\n\n• Registered Entity Name: GRADCONNECT INNOVATIONS PRIVATE LIMITED\n• Corporate Address: B-519, RK Iconic Sheetal Park, Puneet Naga Bajrang, Rajkot Bhomeshwar Plot, Rajkot- 360006, Gujarat\n• Official Electronic Contact: contact@freshmentapp.com\n• Portal Security Status: 2FA Enforced, Active Compliant Startup",
    },
  ];

  const handleScrollTo = (id) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  return (
    <div className="position-relative" style={styles.page}>
      {/* Grid overlay background */}
      <div style={styles.gridOverlay} />

      {/* Decorative premium lighting */}
      <div style={styles.glowLeft} />
      <div style={styles.glowRight} />

      {/* Page Header */}
      <div
        className="text-center mx-auto position-relative"
        style={styles.header}
      >
        <span
          className="d-inline-flex font-mono align-items-center"
          style={styles.badge}
        >
          <Scale style={{ width: "16px", height: "16px", color: "#FF5F14" }} />
          REGULATORY TERMS OF SERVICE
        </span>
        <h1 className="font-display" style={styles.h1}>
          Terms of Use
        </h1>
        <p className="font-mono subtext" style={styles.subtext}>
          EduConnectPro By Freshment (a product of GRADCONNECT INNOVATIONS
          PRIVATE LIMITED) • LAST REVISED: JULY 10, 2026
        </p>
      </div>

      <div
        className="mx-auto d-flex flex-xl-row flex-column position-relative"
        style={styles.contentRow}
      >
        {/* Modern Sidebar Navigation */}
        <div className="col-12 col-xl-4" style={styles.sidebar}>
          <h3 className="font-mono" style={styles.sidebarTitle}>
            TERMS OUTLINE
          </h3>
          <div style={styles.sidebarLinks}>
            {terms.map((sec) => (
              <button
                key={sec.id}
                type="button"
                onClick={() => handleScrollTo(sec.id)}
                className="d-flex title-hover align-items-center justify-content-between"
                style={{
                  ...styles.sidebarButton,
                  ...(activeSection === sec.id
                    ? styles.sidebarButtonActive
                    : styles.sidebarButtonInactive),
                }}
                onMouseDown={(e) => e.preventDefault()}
                onFocus={(e) => {
                  e.currentTarget.style.outline = "none";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <span style={styles.truncate}>{sec.title}</span>

                <ChevronRight
                  style={{
                    width: "16px",
                    height: "16px",
                    color: activeSection === sec.id ? "#FF5F14" : "#cbd5e1",
                    transform:
                      activeSection === sec.id ? "translateX(2px)" : "none",
                    transition: "all .2s ease",
                  }}
                />
              </button>
            ))}
          </div>
        </div>

        {/* Legal Text content */}
        <div
          className="col-12 col-xl-8 position-relative content-card-hover"
          style={styles.contentCard}
        >
          {/* top bar */}
          <div className="content-topbar" style={styles.topBar} />

          <div style={styles.sectionsWrapper}>
            {terms.map((sec, idx) => (
              <div
                key={sec.id}
                id={sec.id}
                style={{
                  ...styles.section,
                  ...(idx === terms.length - 1 ? styles.sectionLast : {}),
                }}
              >
                <h2 style={styles.sectionTitle}>
                  <span style={styles.sectionBar} />
                  {sec.title}
                </h2>
                <p style={styles.sectionContent}>{sec.content}</p>
              </div>
            ))}
          </div>

          {/* Secure compliance notice */}
          <div className="d-flex align-items-start " style={styles.noticeBox}>
            <ShieldCheck
              style={{
                width: "24px",
                height: "24px",
                color: "#FF5F14",
                flexShrink: 0,
                marginTop: "2px",
              }}
            />
            <div style={{ fontSize: "0.75rem" }}>
              <h4 className="font-mono" style={styles.noticeTitle}>
                Platform Integrity Commitment
              </h4>
              <p style={styles.noticeText}>
                Freshment's EduConnectPro SaaS operations are protected by
                industry-standard certifications. Roster files, search filters,
                and recruitment drives are isolated with client-controlled
                security configurations.
              </p>
            </div>
          </div>
        </div>
        <style>{`
  .content-topbar {
    transition: background-color 0.3s ease;
  }
  .content-card-hover:hover .content-topbar {
    background-color: #FF5F14 !important;
  }
    .title-hover:hover {
  color: oklch(20.8% 0.042 265.755) !important;
  background-color: oklch(98.4% 0.003 247.858) !important;
}
  @media (max-width:480px)
  {
        .subtext{font-size:9px !important;}
  }

`}</style>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    backgroundColor: "rgba(248, 250, 252, 0.5)",
    color: "#0f172a",
    padding: "6rem 1rem",
    overflow: "hidden",
  },
  gridOverlay: {
    position: "absolute",
    inset: 0,
    backgroundImage:
      "linear-gradient(to right, #8080800a 1px, transparent 1px), linear-gradient(to bottom, #8080800a 1px, transparent 1px)",
    backgroundSize: "14px 24px",
    maskImage:
      "radial-gradient(ellipse 60% 50% at 50% 0%, #000 70%, transparent 100%)",
    WebkitMaskImage:
      "radial-gradient(ellipse 60% 50% at 50% 0%, #000 70%, transparent 100%)",
    pointerEvents: "none",
  },
  glowLeft: {
    position: "absolute",
    top: "48px",
    left: "25%",
    width: "500px",
    height: "500px",
    borderRadius: "50%",
    backgroundColor: "rgba(255, 95, 20, 0.03)",
    filter: "blur(140px)",
    pointerEvents: "none",
  },
  glowRight: {
    position: "absolute",
    bottom: "48px",
    right: "48px",
    width: "600px",
    height: "600px",
    borderRadius: "50%",
    backgroundColor: "rgba(255, 237, 213, 0.1)",
    filter: "blur(160px)",
    pointerEvents: "none",
  },
  header: {
    maxWidth: "48rem",
    marginBottom: "5rem",
    zIndex: 10,
  },
  badge: {
    gap: "0.375rem",
    padding: "0.375rem 0.875rem",
    borderRadius: "9999px",
    backgroundColor: "#fff7ed",
    border: "1px solid rgba(255, 237, 213, 0.6)",
    color: "#FF5F14",
    fontSize: "0.75rem",
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    fontFamily: "monospace",
    marginBottom: "1rem",
    boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
  },
  h1: {
    fontSize: "2.75rem",
    fontWeight: 900,
    color: "#020617",
    letterSpacing: "-0.025em",
    marginBottom: "1rem",
    lineHeight: 1.1,
  },
  subtext: {
    fontSize: "11px",
    color: "#FF5F14",
    fontFamily: "monospace",
    fontWeight: 800,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
  },
  contentRow: {
    maxWidth: "72rem",
    gap: "2.5rem",
    alignItems: "flex-start",
    zIndex: 10,
  },
  sidebar: {
    padding: "1.5rem",
    borderRadius: "1rem",
    border: "1px solid rgba(226, 232, 240, 0.8)",
    backgroundColor: "#fff",
    position: "sticky",
    top: "6rem",
    boxShadow: "0 20px 25px -5px rgba(241, 245, 249, 0.4)",
  },
  sidebarTitle: {
    fontSize: "10.5px",
    fontFamily: "monospace",
    fontWeight: 700,
    letterSpacing: "0.2em",
    color: "#94a3b8",
    textTransform: "uppercase",
    paddingBottom: "0.75rem",
    borderBottom: "1px solid #f1f5f9",
    marginBottom: "1rem",
  },
  sidebarLinks: {
    display: "flex",
    flexDirection: "column",
    gap: "0.25rem",
    fontSize: "0.75rem",
  },
  sidebarButton: {
    width: "100%",
    textAlign: "left",
    padding: "0.75rem 0.875rem",
    borderRadius: "0.75rem",
    fontWeight: 700,
    cursor: "pointer",
    transition: "all 0.2s ease",
    outline: "none",

    borderTop: "none",
    borderRight: "none",
    borderBottom: "none",
    borderLeft: "3px solid transparent",

    backgroundColor: "transparent",
    appearance: "none",
    WebkitAppearance: "none",
    MozAppearance: "none",
  },

  sidebarButtonActive: {
    backgroundColor: "#fff7ed",
    color: "#FF5F14",
    borderLeft: "3px solid #FF5F14",
    paddingLeft: "0.875rem",
    boxShadow: "none",
  },

  sidebarButtonInactive: {
    backgroundColor: "transparent",
    color: "#64748b",
    borderLeft: "3px solid transparent",
    paddingLeft: "0.875rem",
    boxShadow: "none",
  },
  truncate: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  contentCard: {
    backgroundColor: "#fff",
    border: "1px solid rgba(226, 232, 240, 0.8)",
    borderRadius: "1rem",
    padding: "2.5rem",
    boxShadow: "0 20px 25px -5px rgba(241, 245, 249, 0.4)",
    overflow: "hidden",
  },
  topBar: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    backgroundColor: "rgba(255, 95, 20, 0.1)",
  },

  sectionsWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: "2.5rem",
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
    borderBottom: "1px solid rgba(241, 245, 249, 0.8)",
    paddingBottom: "2rem",
  },
  sectionLast: {
    borderBottom: "none",
    paddingBottom: 0,
  },
  sectionTitle: {
    fontSize: "1.25rem",
    fontWeight: 700,
    letterSpacing: "-0.025em",
    color: "#0f172a",
    display: "flex",
    alignItems: "center",
    gap: "0.625rem",
  },
  sectionBar: {
    width: "6px",
    height: "24px",
    borderRadius: "4px",
    backgroundColor: "#FF5F14",
    display: "inline-block",
  },
  sectionContent: {
    fontSize: "0.875rem",
    color: "#475569",
    lineHeight: 1.625,
    fontWeight: 600,
    whiteSpace: "pre-line",
  },
  noticeBox: {
    marginTop: "3rem",
    padding: "1.5rem",
    borderRadius: "0.75rem",
    border: "1px solid #ffedd5",
    backgroundColor: "rgba(255, 247, 237, 0.5)",
    gap: "1rem",
  },
  noticeTitle: {
    fontWeight: 800,
    color: "#020617",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    fontFamily: "monospace",
    fontSize: "0.75rem",
  },
  noticeText: {
    color: "#475569",
    marginTop: "0.375rem",
    lineHeight: 1.625,
    fontWeight: 500,
  },
};
